import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import { Icon, type IconName } from './Icon';

export type ToastTone = 'success' | 'warning' | 'danger' | 'info';
interface ToastEntry { id: number; tone: ToastTone; title: string; body?: string; expiresAt: number }
interface ToastApi { push: (t: Omit<ToastEntry, 'id' | 'expiresAt'> & { ttl?: number }) => void }
const ToastCtx = createContext<ToastApi | null>(null);
const icons: Record<ToastTone, IconName> = { success: 'checkCircle', warning: 'alert', danger: 'alert', info: 'info' };
export function useToast(): ToastApi {
  const ctx = useContext(ToastCtx);
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>');
  return ctx;
}
export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastEntry[]>([]);
  const reduce = useReducedMotion();
  const dismiss = useCallback((id: number) => setToasts(previous => previous.filter(t => t.id !== id)), []);
  const push = useCallback<ToastApi['push']>(({ ttl, ...t }) => {
    const entry = { ...t, id: Date.now() + Math.random(), expiresAt: Date.now() + (ttl ?? (t.tone === 'success' ? 4000 : 8000)) };
    setToasts(previous => [...previous, entry]);
  }, []);
  useEffect(() => {
    const timers = toasts.map(t => setTimeout(() => dismiss(t.id), Math.max(0, t.expiresAt - Date.now())));
    return () => timers.forEach(clearTimeout);
  }, [toasts, dismiss]);
  const api = useMemo(() => ({ push }), [push]);
  return <ToastCtx.Provider value={api}>{children}
    {typeof document !== 'undefined' && createPortal(<div className="fd-toast-region" aria-live="polite" aria-relevant="additions">
      <AnimatePresence initial={false}>{toasts.map(t => <motion.div key={t.id} layout={!reduce} className={`fd-toast fd-toast--${t.tone}`}
        initial={{ opacity: 0, y: reduce ? 0 : 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: reduce ? 0 : 4 }}
        transition={{ duration: reduce ? 0 : .18 }} role={t.tone === 'danger' ? 'alert' : 'status'}>
        <span className="fd-toast__icon"><Icon name={icons[t.tone]} size={18} /></span>
        <div className="fd-toast__content"><div className="fd-toast__title">{t.title}</div>{t.body && <div className="fd-toast__body">{t.body}</div>}</div>
        <button type="button" className="fd-toast__dismiss" aria-label={document.documentElement.lang.startsWith('de') ? 'Benachrichtigung schließen' : 'Dismiss notification'} onClick={() => dismiss(t.id)}><Icon name="close" size={16} /></button>
      </motion.div>)}</AnimatePresence>
    </div>, document.body)}
  </ToastCtx.Provider>;
}
